import { Box, Button, Divider, FormControl, FormHelperText, InputAdornment, InputLabel, OutlinedInput, TextField, Typography } from "@mui/material";
import { ButtonsContainer, FormularioCard } from "../../styles";
import { FichaTecnica } from "../../../FichaTecnica";
import { yupResolver } from "@hookform/resolvers/yup";
import { FieldValues, useForm } from "react-hook-form";
import { v4 } from "uuid";
import { useMoeda } from "../../../../../core/hooks/useMoeda";
import { useEffect, useState } from "react";
import { IInsumo } from "../../../../interfaces/IInsumo";
import { useCadastrarEditarProdutoContext } from "../../../../contexts/CadastrarEditarProdutoContext";
import { TipoControleEstoque } from "../../../../enum/TipoControleEstoque";
import { IMassa } from "../../../../interfaces/IMassa";
import { massaSchema } from "../../../../schema/massaSchema";
import { IImagemProduto } from "../../../../interfaces/IImagemProduto";
import { useSnackbar } from "../../../../../core/contexts/SnackbarContext";
import { MIMETypes } from "../../../../../core/enum/MIMETypes";
import { ImagemContainer } from "../../../ConfiguracaoPizzaContainer/styles";
import { CardUploadImagem } from "../../../../../core/components/CardUploadImagem";
import { IProcessoPreparoKDS } from "../../../../../kds/interfaces/IKDS";
import { ProcessoPreparoForm } from "../../../../../cozinha/components/ProcessoPreparoForm";
import { useEstacoesKDS } from "../../../../../kds/hooks/useEstacoesKDS";

interface FormularioMassaProps {
    adicionarMassa: (massa: IMassa) => void;
    editarMassa: (massa: IMassa) => void;
    massaEdicao?: IMassa;
    handleTab: (tab: "tabela" | "formulario") => void;
}

export const FormularioMassa = ({ adicionarMassa, handleTab, massaEdicao, editarMassa }: FormularioMassaProps) => {
    const { controleEstoque } = useCadastrarEditarProdutoContext();
    const { number } = useMoeda();
    const { estacoes } = useEstacoesKDS();

    const [imagem, setImagem] = useState<undefined | File | IImagemProduto>(undefined);
    const [fichaTecnica, setFichaTecnica] = useState<IInsumo[]>([]);
    const [processosPreparoKDS, setProcessosPreparoKDS] = useState<IProcessoPreparoKDS[]>([]);

    const { reset, watch, setValue, register, handleSubmit, formState: { errors } } = useForm({
        resolver: yupResolver(massaSchema),
        mode: "onChange",
    });

    const adicionarInsumo = (insumo: IInsumo) => {
        setFichaTecnica(ficha => [...ficha, insumo]);
    }

    const excluirInsumo = (uuid: string) => {
        setFichaTecnica(ficha => ficha.filter(insumo => insumo.uuid !== uuid));
    }

    const handleCadastrarMassa = async (massa: FieldValues) => {
        const imagens: IImagemProduto[] = [];

        if (imagem) {
            imagens.push(imagem as IImagemProduto);
        }

        adicionarMassa({
            descricao: massa.descricao,
            preco: number(massa.preco),
            precoSemDesconto: number(massa.precoSemDesconto),
            uuid: v4(),
            fichaTecnica,
            imagens,
            processosPreparoKDS
        });
        handleTab("tabela")
    }

    const handleEditarMassa = async (massa: FieldValues) => {
        if (massaEdicao) {
            const imagens: IImagemProduto[] = [];

            if (imagem) {
                imagens.push(imagem as IImagemProduto);
            }

            editarMassa({
                descricao: massa.descricao,
                preco: number(massa.preco),
                precoSemDesconto: number(massa.precoSemDesconto),
                uuid: massaEdicao.uuid,
                fichaTecnica,
                imagens,
                processosPreparoKDS
            })
            handleTab("tabela")
        }
    }

    const snackbar = useSnackbar();
    const mimetypesValidos = [
        MIMETypes["image/jpeg"],
        MIMETypes["image/jpg"],
        MIMETypes["image/png"]
    ] as string[];

    const handleUploadImagem = (arquivo: File) => {
        if (!mimetypesValidos.includes(arquivo.type)) {
            return snackbar(`Arquivo inválido, tipos aceitos: (PNG, JPEG)`, { severity: "error" })
        }

        setImagem(arquivo);
    }

    const handleExcluirImagem = () => {
        setImagem(undefined);
    }

    useEffect(() => {
        if (massaEdicao) {
            setValue("descricao", massaEdicao.descricao);
            setValue("preco", massaEdicao.preco);
            setValue("precoSemDesconto", massaEdicao.precoSemDesconto);
            setFichaTecnica(massaEdicao.fichaTecnica ?? []);
            setProcessosPreparoKDS(massaEdicao.processosPreparoKDS ?? []);
            if (massaEdicao.imagens?.length) {
                setImagem(massaEdicao.imagens[0]);
            }
        } else {
            reset();
            setFichaTecnica([]);
            setImagem(undefined);
            setProcessosPreparoKDS([]);
        }
    }, [massaEdicao])

    const descricao = watch("descricao");
    const preco = watch("preco");
    const precoSemDesconto = watch("precoSemDesconto");

    return (
        <FormularioCard>
            <Typography variant="h6">
                Cadastrar massa
            </Typography>
            <form onSubmit={handleSubmit(massaEdicao ? handleEditarMassa : handleCadastrarMassa)}>
                <TextField
                    autoFocus
                    margin="dense"
                    id="name"
                    label="Nome"
                    type="text"
                    fullWidth
                    {...register("descricao")}
                    error={!!errors.descricao}
                    helperText={<>{errors.descricao?.message}</>}
                    value={descricao}
                />
                <FormControl fullWidth sx={{ mt: 3 }}>
                    <InputLabel error={!!errors.preco} htmlFor="preco-massa">Preço</InputLabel>
                    <OutlinedInput
                        id="preco-massa"
                        error={!!errors.preco}
                        startAdornment={<InputAdornment position="start">R$</InputAdornment>}
                        label="Preço"
                        value={preco}
                        {...register("preco")}
                    />
                    {!!errors.preco && <FormHelperText error={true}><>{errors.preco?.message}</></FormHelperText>}
                </FormControl>
                <FormControl fullWidth sx={{ mt: 3 }}>
                    <InputLabel error={!!errors.precoSemDesconto} htmlFor="preco-massa-sem-desconto">Preço sem desconto</InputLabel>
                    <OutlinedInput
                        id="preco-massa-sem-desconto"
                        error={!!errors.precoSemDesconto}
                        startAdornment={<InputAdornment position="start">R$</InputAdornment>}
                        label="Preço sem desconto"
                        value={precoSemDesconto}
                        {...register("precoSemDesconto")}
                    />
                    {!!errors.precoSemDesconto && <FormHelperText error={true}><>{errors.precoSemDesconto?.message}</></FormHelperText>}
                </FormControl>
                <ImagemContainer>
                    <CardUploadImagem
                        titulo="Imagem"
                        descricao={
                            imagem
                                ? (imagem instanceof File ? imagem?.name : (imagem?.nomeOriginal ?? imagem?.nome))
                                : "Faça o upload"
                        }
                        url={imagem instanceof File ? URL.createObjectURL(imagem) : imagem?.url}
                        excluir={handleExcluirImagem}
                        upload={handleUploadImagem}
                    />
                </ImagemContainer>
                {controleEstoque == TipoControleEstoque.completo && (
                    <FichaTecnica fichaTecnica={fichaTecnica} adicionarInsumo={adicionarInsumo} excluirInsumo={excluirInsumo} />
                )}
                <Box sx={{ mt: 3 }}>
                    <Divider sx={{ mb: 2 }} />
                    <ProcessoPreparoForm
                        processos={processosPreparoKDS}
                        estacoes={estacoes}
                        onAdicionar={async (dados) => setProcessosPreparoKDS(prev => [...prev, { ...dados, uuid: v4() }])}
                        onAtualizar={async (uuid, dados) => setProcessosPreparoKDS(prev => prev.map(p => p.uuid === uuid ? { ...p, ...dados } : p))}
                        onRemover={async (uuid) => setProcessosPreparoKDS(prev => prev.filter(p => p.uuid !== uuid).map(p => ({ ...p, dependeDe: p.dependeDe.filter(d => d !== uuid) })))}
                    />
                </Box>
                <ButtonsContainer>
                    <Button variant="outlined" type="button" onClick={() => handleTab("tabela")}>Cancelar</Button>
                    <Button variant="contained" type="submit">{massaEdicao ? "Atualizar" : "Cadastrar"}</Button>
                </ButtonsContainer>
            </form>
        </FormularioCard>
    );
}